'use client'

import {
  reportBrowserError,
  reportNextjsError,
  submitDDEvent,
} from '@/lib/observability/datadog-rum'
import { pageCommit } from '@/lib/observability/page-commit'
import { pageContentReadiness } from '@/lib/observability/page-content-readiness'
import { pageReadiness } from '@/lib/observability/page-readiness'

/**
 * Recovery shared by `app/error.tsx`, `app/global-error.tsx` and the
 * route-group boundaries (`app/(app)/(workspace)/error.tsx`).
 *
 * A deploy replaces the chunk files the currently-loaded build asks for,
 * so a tab left open across the deploy throws `ChunkLoadError` on the next
 * lazy import. `reset()` only re-renders the segment, which requests the
 * same missing chunk again — one hard reload is what actually fixes it,
 * because it pulls the new build's HTML and chunk manifest.
 *
 * Clerk-hosted chunk failures never reach here; `ClerkLoadFailureBoundary`
 * classifies and recovers those on its own.
 */

/**
 * Both boundaries share this key, so the same chunk failure escalating from
 * the route boundary to the root one cannot reload twice.
 */
const RELOAD_ATTEMPTED_KEY = 'chunk-load-reload-attempted'

/**
 * Kept narrow on purpose: only the two shapes bundlers actually emit, so an
 * unrelated error that happens to mention a chunk never triggers a reload.
 * `error.name` is the primary signal; this is the fallback for errors that
 * cross a serialization boundary and lose it.
 */
const CHUNK_LOAD_MESSAGE_PATTERN =
  /chunkloaderror|loading (?:css )?chunk \S+ failed|failed to load (?:css )?chunk \S+/i

export function isChunkLoadError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) {
    return false
  }
  const { name, message } = error as { name?: unknown; message?: unknown }
  if (name === 'ChunkLoadError') {
    return true
  }
  return typeof message === 'string' && CHUNK_LOAD_MESSAGE_PATTERN.test(message)
}

/**
 * Claims the session's single automatic reload. A broken deployment can fail
 * on several differently named chunks in sequence, so a fingerprint-scoped
 * budget can still loop. Manual recovery remains available after this one
 * automatic attempt is spent.
 */
function claimReloadAttempt(): boolean {
  try {
    if (window.sessionStorage.getItem(RELOAD_ATTEMPTED_KEY) === '1') {
      return false
    }
    window.sessionStorage.setItem(RELOAD_ATTEMPTED_KEY, '1')
    return window.sessionStorage.getItem(RELOAD_ATTEMPTED_KEY) === '1'
  } catch {
    return false
  }
}

/**
 * Which boundary caught the error. The root boundary reports without one;
 * a route-group boundary names itself (and the route it rendered for) so
 * RUM can group "the emails canvas threw" apart from "settings threw"
 * without reading stacks.
 */
export type BoundaryReportContext = {
  boundary: string
  route?: string
}

/** RUM action a named boundary emits beside the Next.js error record. */
export const ERROR_BOUNDARY_EVENT = 'next_error_boundary'

/**
 * Reports the caught error and, for a stale-deploy `ChunkLoadError`, spends
 * the session's automatic reload. Anything else keeps the boundary's existing
 * manual-retry fallback.
 *
 * The Next.js error record itself (`reportNextjsError` →
 * `addNextjsError`) takes no context, so a named boundary ALSO emits one
 * `next_error_boundary` action carrying `boundary`, `route`, the error name
 * and the digest — never the message. Errors stay counted once; the action
 * is the attribution dimension.
 */
export function recoverFromBoundaryError(
  error: Error & { digest?: string },
  context?: BoundaryReportContext
): void {
  pageReadiness.fail(context?.route)
  pageCommit.fail(context?.route)
  pageContentReadiness.fail(context?.route)
  if (!isChunkLoadError(error)) {
    reportNextjsError(error)
    if (context) {
      submitDDEvent(ERROR_BOUNDARY_EVENT, {
        boundary: context.boundary,
        ...(context.route ? { route: context.route } : {}),
        errorName: error.name,
        ...(error.digest ? { digest: error.digest } : {}),
      })
    }
    return
  }

  const wasAutoReloaded = claimReloadAttempt()
  reportBrowserError(error, {
    feature: 'chunk-load-recovery',
    recoverable: true,
    wasAutoReloaded,
    ...(context ?? {}),
  })
  if (wasAutoReloaded) {
    window.location.reload()
  }
}
